import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { scrambleElement, cancelScramble } from '../utils/ScrambleText';

gsap.registerPlugin(ScrollTrigger);

/** Selectors for labels that scramble on load (hero area). */
const HERO_ON_LOAD_SELECTORS = [
  '.hero-headline',
  '.hero-panel-title',
  '.hero-scroll-label',
];

/** Selectors for labels that scramble when entering viewport (single element per selector). */
/** .popular-plants-title excluded: animated by App (slide-up + fade on scroll) */
const ON_ENTER_VIEW_SELECTORS: string[] = [
  '.blog-section-label',
  '.blog-section-title',
  '.blog-section-subtitle',
  '.faq-title',
  '.faq-subtitle',
  '.contact-title',
  '.contact-subtitle',
  '.footer-cta-qr-label',
];

/** Selectors that may match multiple elements (each gets its own ScrollTrigger). */
const ON_ENTER_VIEW_MULTI_SELECTORS: string[] = ['.footer-heading'];

/** Blog card titles: many elements, each scrambles when in view. */
const BLOG_CARD_TITLE_SELECTOR = '.blog-card-title';

const SCRAMBLE_DURATION = 0.85;
const HERO_DELAY = 0.9; // After nav entrance
const HERO_STAGGER = 0.12;
const FAQ_SCRAMBLE_DELAY = 0.4; // After FAQ title/subtitle entrance starts

export class ScrambleLabelsController {
  private scrollTriggers: ScrollTrigger[] = [];
  private scrambledElements: HTMLElement[] = [];
  private reduceMotion: boolean = false;

  constructor() {
    this.reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.reduceMotion) return;

    this.runHeroOnLoad();
    this.setupScrollTriggers();
  }

  private runScramble(el: HTMLElement, delay = 0): void {
    if (!el.textContent?.trim()) return;
    this.scrambledElements.push(el);
    scrambleElement(el, {
      duration: SCRAMBLE_DURATION,
      delay,
    });
  }

  private runHeroOnLoad(): void {
    const elements: HTMLElement[] = [];
    HERO_ON_LOAD_SELECTORS.forEach((sel) => {
      const el = document.querySelector(sel) as HTMLElement | null;
      if (el) elements.push(el);
    });
    elements.forEach((el, i) => {
      gsap.delayedCall(HERO_DELAY + i * HERO_STAGGER, () => {
        this.runScramble(el, 0);
      });
    });
  }

  private setupScrollTriggers(): void {
    ON_ENTER_VIEW_SELECTORS.forEach((selector) => {
      const el = document.querySelector(selector) as HTMLElement | null;
      if (!el?.textContent?.trim()) return;

      const isFaq = selector === '.faq-title' || selector === '.faq-subtitle';
      const triggerDelay = isFaq ? FAQ_SCRAMBLE_DELAY : 0;

      const st = ScrollTrigger.create({
        trigger: el,
        start: 'top 85%',
        once: true,
        onEnter: () => {
          if (triggerDelay > 0) {
            gsap.delayedCall(triggerDelay, () => this.runScramble(el, 0));
          } else {
            this.runScramble(el, 0);
          }
        },
      });
      this.scrollTriggers.push(st);
    });

    ON_ENTER_VIEW_MULTI_SELECTORS.forEach((selector) => {
      document.querySelectorAll(selector).forEach((node) => {
        const el = node as HTMLElement;
        if (!el.textContent?.trim()) return;
        const st = ScrollTrigger.create({
          trigger: el,
          start: 'top 85%',
          once: true,
          onEnter: () => this.runScramble(el, 0),
        });
        this.scrollTriggers.push(st);
      });
    });

    const blogTitles = document.querySelectorAll(BLOG_CARD_TITLE_SELECTOR);
    blogTitles.forEach((node) => {
      const el = node as HTMLElement;
      if (!el.textContent?.trim()) return;

      const st = ScrollTrigger.create({
        trigger: el,
        start: 'top 90%',
        once: true,
        onEnter: () => this.runScramble(el, 0),
      });
      this.scrollTriggers.push(st);
    });
  }

  public dispose(): void {
    this.scrollTriggers.forEach((st) => st.kill());
    this.scrollTriggers = [];
    this.scrambledElements.forEach((el) => cancelScramble(el));
    this.scrambledElements = [];
  }
}
